import React, { useState } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  FormHelperText,
  IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { employeeAPI, EmployeeFormData } from '../services/api';

const EmployeeAdd: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<EmployeeFormData>({
    employeeCode: '',
    name: '',
    username: '',
    password: '',
    confirmPassword: '',
    phone: '',
    email: '',
    status: 'เปิดการใช้งาน'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // ล้าง error เมื่อมีการแก้ไขข้อมูล
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.employeeCode) {
      newErrors.employeeCode = 'กรุณาระบุรหัสประจำตัว';
    }
    
    if (!formData.name) {
      newErrors.name = 'กรุณาระบุชื่อพนักงาน';
    }
    
    if (!formData.username) {
      newErrors.username = 'กรุณาระบุ Username';
    }
    
    if (!formData.password) {
      newErrors.password = 'กรุณาระบุรหัสผ่าน';
    } else if (formData.password.length < 6) {
      newErrors.password = 'รหัสผ่านต้องมีความยาวอย่างน้อย 6 ตัวอักษร';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'กรุณายืนยันรหัสผ่าน';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'รหัสผ่านไม่ตรงกัน';
    }
    
    if (!formData.phone) {
      newErrors.phone = 'กรุณาระบุเบอร์โทร';
    }
    
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'รูปแบบอีเมลไม่ถูกต้อง';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      await employeeAPI.addEmployee(formData);
      // แสดง success message หรือ redirect
      navigate('/employees');
    } catch (error) {
      console.error('Error adding employee:', error);
      // แสดง error message
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/employees');
  };

  return (
    <Box sx={{ 
      p: { xs: 2, sm: 3, md: 4 },
      maxWidth: '100%'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton 
          onClick={() => navigate('/employees')}
          sx={{ mr: 2 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ color: '#114776', fontWeight: 500 }}>
          เพิ่มรายชื่อพนักงาน
        </Typography>
      </Box>

      <Paper 
        component="form" 
        onSubmit={handleSubmit}
        elevation={2} 
        sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box 
              sx={{ 
                width: 200, 
                height: 200, 
                bgcolor: '#f5f5f5', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                borderRadius: '4px',
                mb: 2,
                position: 'relative'
              }}
            >
              <PhotoCameraIcon sx={{ fontSize: 40, color: '#aaa' }} />
              <IconButton 
                sx={{ 
                  position: 'absolute', 
                  bottom: 10, 
                  right: 10, 
                  bgcolor: 'white',
                  '&:hover': { bgcolor: '#f0f0f0' }
                }}
              >
                <PhotoCameraIcon fontSize="small" />
              </IconButton>
            </Box>
            <Button variant="outlined" size="small">
              แก้ไขรูป
            </Button>
          </Grid>

          <Grid item xs={12} md={9}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="รหัสประจำตัว"
                  name="employeeCode"
                  value={formData.employeeCode}
                  onChange={handleTextChange}
                  error={!!errors.employeeCode}
                  helperText={errors.employeeCode}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="ชื่อพนักงาน"
                  name="name"
                  value={formData.name}
                  onChange={handleTextChange}
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Username"
                  name="username"
                  value={formData.username}
                  onChange={handleTextChange}
                  error={!!errors.username}
                  helperText={errors.username}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Password"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleTextChange}
                  error={!!errors.password}
                  helperText={errors.password}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Confirm Password"
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleTextChange}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="เบอร์โทร"
                  name="phone"
                  value={formData.phone}
                  onChange={handleTextChange}
                  error={!!errors.phone}
                  helperText={errors.phone}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleTextChange}
                  error={!!errors.email}
                  helperText={errors.email}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>สถานะการใช้งาน</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    label="สถานะการใช้งาน"
                    onChange={handleSelectChange}
                  >
                    <MenuItem value="เปิดการใช้งาน">เปิดการใช้งาน</MenuItem>
                    <MenuItem value="ปิดการใช้งาน">ปิดการใช้งาน</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={handleCancel}
            disabled={loading}
          >
            ยกเลิก
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            sx={{ bgcolor: '#114776', '&:hover': { bgcolor: '#1e40af' } }}
            disabled={loading}
          >
            บันทึก
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default EmployeeAdd; 